import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Building2, Target, Wrench, TrendingUp, Star,
  ExternalLink, FileText, Package, DollarSign, ChevronRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { useCase } from '@/hooks/usePartnerships';
import { WinclapLoader } from '@/components/ui/winclap-loader';

export function CaseDetailPage() {
  const { t } = useTranslation();
  const { partner: slug, id: caseId } = useParams<{ partner: string; id: string }>();
  const { data: caseItem, isLoading } = useCase(caseId || '');

  if (isLoading) return <div className="flex items-center justify-center py-24"><WinclapLoader /></div>;
  if (!caseItem) return <div className="py-16 text-center text-muted-foreground">{t('partnerships.case_detail.not_found')}</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to={`/partnerships/${slug}`} className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {caseItem.market && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  <MapPin className="h-2.5 w-2.5" />{caseItem.market}
                </span>
              )}
              {caseItem.industry && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  <Building2 className="h-2.5 w-2.5" />{caseItem.industry}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold mt-1">{caseItem.title}</h1>
            {caseItem.brand && <p className="text-muted-foreground">{caseItem.brand}</p>}
          </div>
        </div>
      </div>

      {/* Drive links */}
      {caseItem.driveLinks && caseItem.driveLinks.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{t('partnerships.case_detail.drive_materials')}</p>
          <div className="flex flex-wrap gap-2">
            {caseItem.driveLinks.map((url, i) => (
              <a
                key={i}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-card hover:bg-muted transition-colors text-sm font-medium group"
              >
                <FileText className="h-4 w-4 text-primary shrink-0" />
                {t('partnerships.case_detail.document_label', { num: i + 1 })}
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {caseItem.summary && (
        <Card className="p-5 border-primary/20 bg-primary/5 space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{t('partnerships.case_detail.executive_summary')}</p>
          <p className="text-sm leading-relaxed font-medium">{caseItem.summary}</p>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {caseItem.objective && (
          <Card className="p-5 space-y-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-500" />
              {t('partnerships.case_detail.objective')}
            </h2>
            <p className="text-sm text-muted-foreground">{caseItem.objective}</p>
          </Card>
        )}

        {caseItem.challenge && (
          <Card className="p-5 space-y-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <ChevronRight className="h-4 w-4 text-red-400" />
              {t('partnerships.case_detail.challenge')}
            </h2>
            <p className="text-sm text-muted-foreground">{caseItem.challenge}</p>
          </Card>
        )}

        {caseItem.solution && (
          <Card className="p-5 space-y-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Wrench className="h-4 w-4 text-orange-500" />
              {t('partnerships.case_detail.solution')}
            </h2>
            <p className="text-sm text-muted-foreground">{caseItem.solution}</p>
          </Card>
        )}

        {caseItem.mediaSpend && (
          <Card className="p-5 space-y-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              {t('partnerships.case_detail.media_investment')}
            </h2>
            <p className="text-sm text-muted-foreground">{caseItem.mediaSpend}</p>
          </Card>
        )}
      </div>

      {caseItem.kpis && caseItem.kpis.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {caseItem.kpis.map((kpi, i) => (
            <Card key={i} className="p-4 text-center">
              <p className="text-sm font-semibold">{kpi}</p>
            </Card>
          ))}
        </div>
      )}

      {caseItem.results.length > 0 && (
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-500" />
            {t('partnerships.case_detail.results')}
          </h2>
          <ul className="space-y-2">
            {caseItem.results.map((r, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <TrendingUp className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                {r}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {caseItem.highlights.length > 0 && (
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Star className="h-4 w-4 text-yellow-500" />
            {t('partnerships.case_detail.highlights')}
          </h2>
          <ul className="space-y-2">
            {caseItem.highlights.map((h, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <Star className="h-3 w-3 mt-0.5 text-yellow-400 shrink-0" />
                {h}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {((caseItem.productsUsed && caseItem.productsUsed.length > 0) ||
        (caseItem.packagesUsed && caseItem.packagesUsed.length > 0)) && (
        <div className="grid gap-4 sm:grid-cols-2">
          {caseItem.productsUsed && caseItem.productsUsed.length > 0 && (
            <Card className="p-5 space-y-3">
              <h2 className="text-sm font-semibold">{t('partnerships.case_detail.products_used')}</h2>
              <ul className="space-y-1">
                {caseItem.productsUsed.map((p, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/50 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {caseItem.packagesUsed && caseItem.packagesUsed.length > 0 && (
            <Card className="p-5 space-y-3">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Package className="h-4 w-4 text-indigo-500" />
                {t('partnerships.case_detail.packages_used')}
              </h2>
              <ul className="space-y-1">
                {caseItem.packagesUsed.map((p, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
